import { deviceTZ, setUserTZ, todayKey } from '../lib/datetime';
import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { fetchMyProfile } from '../lib/myProfile'
import { redeemPendingInvite, resetRedeemedInvites } from '../lib/pendingInvite'
import { logEvent } from '../lib/analytics'
import { APP_VERSION } from '../lib/appVersion'
import { syncCoachSeenFromProfile } from '../lib/coachmarks'
import { syncSeenVersionFromProfile } from '../lib/seenVersion'
import { syncSnoozeFromProfile } from '../lib/checkinSnooze'
import { syncActivityDatesFromProfile } from '../lib/activityStreak'
import { syncTonightTogetherDismissedFromProfile } from '../lib/tonightTogetherDismiss'
import { syncTonightDismissedFromProfile } from '../lib/tonightDismiss'
import { syncNameFromProfile, clearCachedName } from '../lib/displayNameCache'
import { carryGuestList } from '../lib/guestCarry'

// Module-level guards: prevent the same uid from being profile-loaded
// repeatedly across auth state events (INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED
// all fire on initial app load and were triggering 3+ identical fetches).
const loadedUids = new Set()        // uids whose profile has been fetched this session
const lastActiveSent = new Set()    // uids whose last_active was already patched this session
// useAuth() is called independently by MANY components (App.jsx, FaceOff.jsx,
// Cliffhanger.jsx, …) — there's no shared context, so each call site gets its
// OWN local `profile` state. Without this cache, only the FIRST useAuth()
// instance to win the loadedUids race ever actually receives profile data;
// every other instance's loadProfile() call hit the dedup guard above and
// returned WITHOUT calling setProfile, so `profile` stayed null forever in
// that component — is_admin-gated features reading their own useAuth() (the
// Face-Off/Cliffhanger admin FAB check) silently never saw an admin account
// as an admin unless they happened to be the very first mount (caught
// 2026-08-23 debugging why isAdmin never resolved true in Face-Off). Caching
// the last-fetched row here lets every LATER instance seed itself from it
// instead of getting stuck at null.
const profileCache = new Map()      // uid → last-fetched profile row
const anonLibraryCache = new Map()  // anon uid → has a watchlist (is a /start guest, not a co-watch guest)

export function useAuth() {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const inflightUid = useRef(null)  // dedupe concurrent loadProfile calls

  const loadProfile = useCallback(async (uid, { force = false } = {}) => {
    if (!uid) { setProfile(null); return }
    // Skip if we already loaded this uid this session (unless forced after edit)
    // — but still seed THIS instance from the cached row rather than leaving
    // its local `profile` state stuck at null (see the module comment above).
    if (!force && loadedUids.has(uid)) {
      if (profileCache.has(uid)) setProfile(profileCache.get(uid))
      return
    }
    if (inflightUid.current === uid) return  // concurrent call dedup
    inflightUid.current = uid

    // Own full profile (incl. private columns) via SECURITY DEFINER RPC — base
    // profiles SELECT is column-scoped to social fields for `authenticated` (RLS
    // Phase 2), so a direct select('*') would omit prefs/timezone/invite code.
    const { data, error } = await fetchMyProfile(uid)

    if (data) {
      setProfile(data)
      // Pull every coachmark this ACCOUNT has ever dismissed (any device) into
      // this device's localStorage, so a fresh login (cleared cache, new
      // browser, second device) doesn't replay tips already seen elsewhere
      // (bug report 2026-08-17: logging out and back in reset every one).
      syncCoachSeenFromProfile(data)
      // Same fix, applied to WhatsNew's "seen version" flag (bug report 2026-08-18:
      // the changelog toast + full sheet replaying on login/logout — coachmarks got
      // this fix yesterday, this flag was the other half of the same class).
      syncSeenVersionFromProfile(data)
      // Same fix again, applied to the "did you watch this yet" check-in snooze
      // (bug report 2026-08-21: fired twice within 20 minutes across two devices).
      syncSnoozeFromProfile(data)
      // Same fix again — the watch streak (nextep_activity_dates) was localStorage-only,
      // so switching devices mid-streak made a genuinely-daily watcher look broken (2026-08-21 audit).
      syncActivityDatesFromProfile(data)
      syncTonightTogetherDismissedFromProfile(data)
      syncTonightDismissedFromProfile(data, todayKey())
      // Same fix once more — the Home greeting guessed a name from the email while
      // this RPC was in flight, which flashed "Craig" (craig219@… minus the digits)
      // for up to 4s before "Jason" arrived (Jason 2026-08-22).
      syncNameFromProfile(data)
      // Google SSO users arrive with a real photo in identity metadata, but nothing
      // ever wrote it to profiles.avatar_url — so every SSO user showed the gradient
      // initial ("can't change profile picture", user report 2026-08-22). Backfill it
      // ONCE: only when avatar_url is empty, so a future custom upload is never
      // clobbered by the Google photo on the next sign-in.
      if (!data.avatar_url) {
        try {
          const { data: { user: au } } = await supabase.auth.getUser()
          const pic = au?.user_metadata?.avatar_url || au?.user_metadata?.picture
          if (pic && /^https:\/\//.test(pic)) {
            supabase.from('profiles').update({ avatar_url: pic }).eq('id', uid)
              .then(() => setProfile(p => (p && p.id === uid && !p.avatar_url) ? { ...p, avatar_url: pic } : p), () => {})
          }
        } catch { /* metadata unavailable — keep the gradient initial */ }
      }
      try {
        const dev = deviceTZ()
        setUserTZ(data.timezone || dev)
        if (data.timezone_auto !== false && data.timezone !== dev) {
          supabase.from('profiles').update({ timezone: dev }).eq('id', uid).then(() => {}).catch(() => {})
          setUserTZ(dev)
        }
      } catch (e) {}
      loadedUids.add(uid)
      profileCache.set(uid, data)
      // last_active: once per session, not per auth event
      if (!lastActiveSent.has(uid)) {
        lastActiveSent.add(uid)
        supabase.from('profiles').update({ last_active: new Date().toISOString() })
          .eq('id', uid).then(() => {}).catch(() => {})
        // Funnel: the authenticated app actually BOOTED and loaded this user's profile.
        // This is the step that was invisible — a signup can create the account + a
        // trigger-made profile server-side yet the running app never reach here (a failed
        // session/profile load, the iOS PWA split). Ghost signups with last_active=null died
        // exactly here. `new_account` marks the very first boot ever on this device, so we
        // can measure signup_link_sent → first real entry. Emitted once per session, right
        // where last_active is written, so it can't double-count auth events.
        let firstEver = false
        try {
          firstEver = !localStorage.getItem('nextep_booted_before')
          if (firstEver) localStorage.setItem('nextep_booted_before', '1')
        } catch { /* storage blocked, treat as returning, never block boot */ }
        // v = the build this boot is RUNNING (2026-11-01: "no update came across" could not be
        // read from telemetry — five boots in 13s and no way to tell which version they were on).
        logEvent('authed_boot', { new_account: firstEver, v: APP_VERSION })
      }
    } else if (error) {
      // The RPC FAILED (transient/network/RLS hiccup) — data is null but the profile
      // may well exist. Do NOT assume it's missing and do NOT create a fallback: the
      // upsert below used to clobber an existing display_name to the email prefix
      // (Jason's "Jason" → "craig219", 2026-07-06). Leave state untouched and let a
      // later auth event / refresh retry cleanly — don't mark this uid loaded.
      inflightUid.current = null
      return
    } else {
      // Genuinely no row (RPC succeeded, returned nothing) — create it, but
      // non-destructively (ignoreDuplicates → INSERT ON CONFLICT DO NOTHING) so a
      // race can never overwrite an existing profile's name/privacy.
      const { data: { user } } = await supabase.auth.getUser()
      const fallbackName = user?.email?.split('@')[0] || 'User'
      await supabase
        .from('profiles')
        .upsert({ id: uid, display_name: fallbackName, is_public: true }, { onConflict: 'id', ignoreDuplicates: true })
      // Re-read the full own row via the definer RPC (RETURNING * would hit the
      // column-scoped SELECT grant); gives us prefs/timezone the app expects.
      const { data: created } = await supabase.rpc('get_my_profile').maybeSingle()
      setProfile(created || null)
      try { const dev = deviceTZ(); setUserTZ(dev); if (created) supabase.from('profiles').update({ timezone: dev }).eq('id', uid).then(() => {}).catch(() => {}) } catch (e) {}
      if (created) { loadedUids.add(uid); profileCache.set(uid, created) }
    }
    inflightUid.current = null
  }, [])

  useEffect(() => {
    // ── Auth bridge: read cookie set by Safari after magic link click ──────
    // On iOS, the home-screen PWA has isolated localStorage from Safari, so
    // magic link sessions land in Safari's storage. If we find the bridge cookie
    // (set by main.jsx in the Safari context), exchange it for a real session
    // here in the PWA context so we only need the magic link once per install.
    const tryBridge = async () => {
      try {
        // sessionStorage copy first (same tab, survives "Block All Cookies"), cookie second
        // (the iOS Safari → home-screen PWA hand-off). Both are one-shot.
        let raw = null
        try { raw = sessionStorage.getItem('nxab'); if (raw) sessionStorage.removeItem('nxab') } catch {}
        const match = document.cookie.match(/(?:^|; )nxab=([^;]*)/)
        if (!raw && !match) return false
        if (!raw) raw = decodeURIComponent(match[1])
        // Clear immediately to prevent reuse
        document.cookie = 'nxab=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure'
        const { at, rt, ts } = JSON.parse(atob(raw))
        // Reject if cookie is older than 5 minutes
        if (!at || !rt || Date.now() - ts > 5 * 60 * 1000) return false
        const { data, error } = await supabase.auth.setSession({ access_token: at, refresh_token: rt })
        if (error || !data?.session) return false
        return true
      } catch { return false }
    }

    // Is this session an app user? Real accounts always. Anonymous sessions only when
    // they own a library (≥1 watchlist row) — that's a /start onboarding guest, not a
    // name-only co-watch guest (0 shows, measured cleanly separable: 19 vs 3, no overlap).
    // One HEAD query per anonymous boot, cached per uid for the session.
    const asAppUser = async (u) => {
      if (!u) return null
      if (!u.is_anonymous) return u
      if (anonLibraryCache.has(u.id)) return anonLibraryCache.get(u.id) ? u : null
      try {
        const { count } = await supabase.from('watchlist').select('user_id', { count: 'exact', head: true }).eq('user_id', u.id)
        if (anonLibraryCache.get(u.id) === true) return u
        const has = (count || 0) > 0
        anonLibraryCache.set(u.id, has)
        return has ? u : null
      } catch { return null }
    }

    // Watchdog: never let a rejected/hung getSession() pin the whole app on the
    // loading splash. App.jsx gates every screen on `loading`, and a synchronous
    // storage-access throw (iOS "Block All Cookies", restricted in-app webviews
    // magic-link users open links inside) can reject getSession() before
    // setLoading(false) runs — with no error boundary to catch an async rejection,
    // the user would be stuck on the splash and have to force-quit (audit 2026-07-06).
    const bootWatchdog = setTimeout(() => setLoading(false), 8000)

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      // Common cold-boot path: a session already exists → use it, NO second getSession
      // (that redundant round-trip gated the loading splash on every logged-in boot).
      // Only re-fetch when there's no session AND the magic-link bridge cookie actually
      // exchanged one (tryBridge → setSession updates the stored session).
      let s = session
      // A stale ANONYMOUS session (guest onboarding, co-watch join) must not block the
      // bridge: the real sign-in's tokens are in the cookie, but getSession() returns
      // the anon user, so the exchange never ran. Half of the Google + magic-link signups
      // that started on /start ended here with zero events (measured 2026-09-08).
      if ((!s || s.user?.is_anonymous) && await tryBridge()) {
        s = (await supabase.auth.getSession()).data.session
      }
      if (s) {
        // An ANONYMOUS session (a name-only co-watch guest) is NOT an app user: it has no
        // profile, and treating it as logged-in dumps the guest into the full app + new-user
        // onboarding around the /join room (Jason 2026-07-25). JoinRoom reads the anon
        // session directly, so the guest flow still works while the app stays logged-out.
        //
        // EXCEPT a guest who built a library in /start onboarding (2026-09-12): that rule
        // was written before guest onboarding existed, and it locked every returning
        // guest out of their own shows — measured: 19 guests with 100 shows between them,
        // and 4 of the 5 who came back were shown the logged-out marketing page. One Play
        // install added 19 shows on Sep 5, bounced off the landing page 7 times, and
        // came back a week later to a dead onboarding again. Library = app user.
        const appU = await asAppUser(s.user)
        setUser(appU)
        if (appU) { redeemPendingInvite(s); loadProfile(appU.id) }
        setLoading(false)
        return
      }
      // No session from getSession(). On a cold PWA/TWA boot the persisted session can
      // load from the async storage adapter AFTER getSession() resolves, then arrive via
      // onAuthStateChange('INITIAL_SESSION'). Resolving to logged-out HERE flashes
      // GuestHome for a returning user (the old localStorage-token probe also misses on a
      // cold boot, before the key is visible). So do NOT touch user/loading here — let
      // INITIAL_SESSION be the single authority for the initial state below. bootWatchdog
      // (8s) is the ultimate safety net if that event somehow never fires.
    }).catch(() => {
      // getSession() threw (storage blocked, restricted webview). Leave resolution to the
      // INITIAL_SESSION event / bootWatchdog rather than flashing a logged-out screen.
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Anonymous sessions are not app users UNLESS they own a library — see asAppUser
      // above. The anon case needs one async lookup, so route it through a promise and
      // fall through with the resolved user; real accounts resolve synchronously.
      if (session?.user?.is_anonymous) {
        asAppUser(session.user).then(u => {
          setUser(u)
          if (session || event === 'INITIAL_SESSION') setLoading(false)
          if (u) loadProfile(u.id)
        })
        return
      }
      const appUser = session?.user && !session.user.is_anonymous ? session.user : null
      const newUid = appUser?.id
      setUser(appUser)
      // onAuthStateChange is the single authority for the initial state. INITIAL_SESSION
      // fires once on boot (with the real session OR null) after the storage adapter has
      // been read, so resolve the splash on it either way; any real session also clears it.
      // This is what closes the cold-boot GuestHome flash — getSession() no longer races it.
      if (session || event === 'INITIAL_SESSION') setLoading(false)
      // On SIGNED_OUT, clear the cache so next sign-in is fresh
      if (event === 'SIGNED_OUT') {
        loadedUids.clear()
        profileCache.clear()
        lastActiveSent.clear()
        resetRedeemedInvites()   // a different user in this tab starts clean
        clearCachedName()        // never show the previous user's name to the next sign-in
        setProfile(null)
        return
      }
      // Token refreshes don't need a profile reload — skip them entirely
      if (event === 'TOKEN_REFRESHED') return
      // Fresh sign-in (incl. the magic-link redirect ingested by detectSessionInUrl):
      // replay any pending invite so the recipient is attributed to their inviter.
      if (event === 'SIGNED_IN' && session && appUser) redeemPendingInvite(session)
      if (appUser) carryGuestList(supabase, appUser.id).catch(() => {})
      loadProfile(newUid)
    })
    // A /start guest is created with ZERO rows, so asAppUser caches "no library" for the
    // session and App kept rendering the logged-out GuestHome after onboarding until a
    // reload (found by a headless onboarding run, 2026-09-15). The guest's first add
    // flips the answer: bust the cache and re-resolve the session as an app user.
    const onLibraryChanged = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        const u = session?.user
        if (!u?.is_anonymous) return
        anonLibraryCache.delete(u.id)
        const appUser = await asAppUser(u)
        if (appUser) { setUser(appUser); loadProfile(appUser.id) }
      } catch {}
    }
    window.addEventListener('nextep:show_added', onLibraryChanged)
    window.addEventListener('nextep:guest-library-changed', onLibraryChanged)
    return () => { clearTimeout(bootWatchdog); subscription.unsubscribe(); window.removeEventListener('nextep:show_added', onLibraryChanged); window.removeEventListener('nextep:guest-library-changed', onLibraryChanged) }
  }, [loadProfile])

  const signInWithEmail = async (email) => {
    const redirectTo = `${window.location.origin}/`

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo }
    })
    return { error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
  }

  const refreshProfile = useCallback(() => loadProfile(user?.id, { force: true }), [user, loadProfile])

  return { user, profile, loading, signInWithEmail, signOut, refreshProfile }
}
